import React from "react";
import { motion } from "framer-motion";
import {
  FaTruck,
  FaStore,
  FaHandshake,
  FaBoxOpen,
  FaShoppingBasket,
} from "react-icons/fa";
import { useMyContext } from "../Context/MyContext";

const servicesData = [
  {
    icon: FaTruck,
    title: "Swift Delivery",
    description:
      "We deliver quality products safely and conveniently to your doorstep.",
  },
  {
    icon: FaStore,
    title: "Wholesale & Retail",
    description:
      "Supplying individuals and businesses with reliable products at affordable prices.",
  },
  {
    icon: FaHandshake,
    title: "Corporate Supply",
    description:
      "Your trusted partner for bulk product supplies to hotels, restaurants, offices, and businesses.",
  },
  {
    icon: FaBoxOpen,
    title: "Quality Products",
    description:
      "Carefully selected, durable, and dependable products you can trust.",
  },
  {
    icon: FaShoppingBasket,
    title: "Affordable Pricing",
    description:
      "Quality products at competitive prices designed to suit every budget.",
  },
];

const iconAnimations = [
  {
    animate: {
      x: [0, -10, 10, -10, 10, 0],
      transition: {
        repeat: Infinity,
        duration: 2,
      },
    },
  },
  {
    animate: {
      y: [0, -10, 0],
      transition: {
        repeat: Infinity,
        duration: 1.5,
      },
    },
  },
  {
    animate: {
      scale: [1, 1.2, 1],
      transition: {
        repeat: Infinity,
        duration: 1.8,
      },
    },
  },
  {
    animate: {
      rotate: [0, 10, -10, 10, -10, 0],
      transition: {
        repeat: Infinity,
        duration: 2.5,
      },
    },
  },
  {
    animate: {
      opacity: [0.8, 1, 0.8],
      y: [0, -5, 0],
      transition: {
        repeat: Infinity,
        duration: 2.2,
      },
    },
  },
];

const AboutUs = () => {
  const { comments } = useMyContext();

  return (
    <section
      id="about"
      className="bg-white text-gray-800 py-12 px-5 md:px-20"
    >
      {/* Title Section */}
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
          About CHAREX
        </h2>

        <p className="text-sm text-gray-500">
          Home &gt;{" "}
          <span className="text-[#7b2cbf] font-bold">About Us</span>
        </p>
      </div>

      {/* Main Info Section */}
      <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
        {/* Image */}
        <div>
          <img
            src="/images/charexaboutimage.jpg"
            alt="About CHAREX"
            className="w-full h-auto rounded-lg shadow-lg object-cover"
          />
        </div>

        {/* Description */}
        <div>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong className="text-[#7b2cbf]">CHAREX</strong> is your trusted
            shopping destination for quality and affordable products. We provide
            carefully selected items designed to meet the everyday needs of
            individuals, families, and businesses.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            From household appliances and fashion items to furniture,
            electronics, perfumes, school essentials, and more, our goal is to
            make shopping simple, convenient, and reliable. At CHAREX, we do not
            just sell products — we deliver value, trust, and customer
            satisfaction.
          </p>

          <p className="text-lg text-[#45a864] font-semibold italic">
            Shop Smart. Save More. Smile More.
          </p>
        </div>
      </div>

      {/* Services */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold mb-6">Our Services</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service, index) => {
            const IconComponent = service.icon;

            return (
              <div
                key={index}
                className="bg-gradient-to-b from-white to-[#faf7ff] border border-gray-100 shadow-md rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#7b2cbf]/40"
              >
                <motion.div
                  className="flex justify-center mb-4"
                  animate={
                    iconAnimations[index % iconAnimations.length].animate
                  }
                >
                  <div className="w-16 h-16 rounded-full bg-[#f4eaff] flex items-center justify-center">
                    <IconComponent size={40} className="text-[#7b2cbf]" />
                  </div>
                </motion.div>

                <h3 className="text-xl font-bold mb-2 text-gray-800">
                  {service.title}
                </h3>

                <p className="text-gray-600">{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Vision & Mission Section */}
      <h3 className="text-3xl font-bold text-gray-900 text-center uppercase tracking-wide mb-8">
        Our Vision & Mission
      </h3>

      <div>
        <h4 className="text-2xl font-semibold text-gray-800 mb-4">
          Vision
        </h4>

        <p className="text-lg text-gray-700 bg-gradient-to-r from-[#faf7ff] via-white to-[#f4fff7] p-4 border-l-4 border-[#7b2cbf] rounded-lg shadow-md mb-8">
          To become a trusted shopping brand that makes quality and affordable
          products easily accessible to individuals, families, and businesses.
        </p>

        <h4 className="text-2xl font-semibold text-gray-800 mb-4">
          Mission
        </h4>

        <p className="text-lg text-gray-700 bg-gradient-to-r from-[#faf7ff] via-white to-[#f4fff7] p-4 border-l-4 border-[#45a864] rounded-lg shadow-md">
          To provide quality, affordable, and reliable products through a
          convenient shopping experience, dependable delivery, and excellent
          customer service.
        </p>
      </div>

      {/* Testimonials Section
      <div id="testimonials">
        <h3 className="text-2xl font-bold text-gray-800 text-center uppercase tracking-wide m-8">
          What Our Clients Say
        </h3>

        {comments.length === 0 ? (
          <p className="text-center text-gray-500">No testimonials yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {comments.map((comment, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <img
                  src={comment.photoURL || "/images/default-user.jpg"}
                  alt={comment.name || "Anonymous"}
                  className="w-16 h-16 rounded-full object-cover mb-4"
                />

                <p className="text-gray-700 italic mb-4">
                  “{comment.text}”
                </p>

                <h4 className="text-lg font-semibold text-gray-800">
                  {comment.name || "Anonymous"}
                </h4>

                {comment.phone && (
                  <p className="text-sm text-gray-600">
                    📞 {comment.phone}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div> */}
    </section>
  );
};

export default AboutUs;